// Desglose del crédito de la Cotización: precio, enganche, financiado, intereses e IVA y el total.
// Lo comparten el paso «Cotizar» del wizard y el cotizador del Portal Vendedor; montos en pesos.
import { formatMXN } from '@/shared/lib/format/money';
import { INTEREST_VAT_RATE } from '@/shared/domain/quote';
import type { PromoSavings } from '../promoSavings';
import { PromoChip } from './PromoChip';
import { QuoteBonusNote } from './QuoteBonusNote';

export interface CreditBreakdownNumbers {
    price: number;
    down: number;
    financed: number;
    interest: number;
    iva: number;
    /** TNA con la que se calcularon los intereses. */
    tna: number;
    weekly: number;
    weeks: number;
    /** Enganche + cuotas: lo que paga el cliente por el crédito. */
    total: number;
}

export function CreditBreakdown({
    numbers: n,
    priceLabel,
    savings,
    bonusCents,
    cashCents = 0,
    openingFee = null,
    creditCost = null,
    ivaRate = INTEREST_VAT_RATE,
}: {
    numbers: CreditBreakdownNumbers;
    priceLabel: string;
    /** Ahorro de la promoción de tasa; `null` = no hay nada que tachar. */
    savings: PromoSavings | null;
    bonusCents?: number;
    /** Lo que se paga de contado aparte del crédito, en cents. */
    cashCents?: number;
    /** Comisión de apertura cuando el plan la cobra. */
    openingFee?: { pct: number; amount: number } | null;
    /** Lo que cuesta el crédito sobre el precio de contado. */
    creditCost?: number | null;
    ivaRate?: number;
}) {
    return (
        <div className="rounded-[10px] border border-border bg-card px-4 py-3 text-[12.5px]">
            <div className="flex justify-between py-1.5">
                <span className="text-muted-foreground">{priceLabel}</span>
                <span className="tabular-nums">{formatMXN(n.price)}</span>
            </div>
            <div className="flex justify-between py-1.5">
                <span className="text-muted-foreground">Enganche</span>
                <span className="tabular-nums text-destructive">− {formatMXN(n.down)}</span>
            </div>
            <div className="flex justify-between py-1.5">
                <span className="text-muted-foreground">Monto a financiar</span>
                <span className="tabular-nums">{formatMXN(n.financed)}</span>
            </div>
            {openingFee !== null && (
                <div className="flex justify-between py-1.5">
                    <span className="text-muted-foreground">
                        Comisión por apertura ({Math.round(openingFee.pct * 1000) / 10}%)
                    </span>
                    <span className="tabular-nums text-warning">
                        + {formatMXN(openingFee.amount)}
                    </span>
                </div>
            )}
            <div className="flex justify-between py-1.5">
                {savings !== null ? (
                    <span className="text-muted-foreground">
                        Intereses (
                        <s className="opacity-60">{Math.round(savings.baseTna * 100)}%</s>{' '}
                        <span className="font-bold text-promo">
                            {Math.round(n.tna * 100)}% anual
                        </span>
                        )
                    </span>
                ) : (
                    <span className="text-muted-foreground">
                        Intereses ({Math.round(n.tna * 100)}% anual)
                    </span>
                )}
                <span className="tabular-nums text-warning">
                    {savings !== null && (
                        <s className="mr-1.5 text-[11.5px] text-muted-foreground">
                            {formatMXN(savings.interestWithout)}
                        </s>
                    )}
                    + {formatMXN(n.interest)}
                </span>
            </div>
            <div className="flex justify-between border-b border-border py-1.5">
                <span className="text-muted-foreground">
                    IVA sobre intereses ({Math.round(ivaRate * 100)}%)
                </span>
                <span className="tabular-nums text-warning">+ {formatMXN(n.iva)}</span>
            </div>
            <div className="flex justify-between pt-2 text-[13.5px] font-bold">
                <span>{cashCents > 0 ? 'Total del crédito' : 'Total que paga el cliente'}</span>
                <span className="tabular-nums">{formatMXN(n.total)}</span>
            </div>
            <div className="pt-1 text-right text-[11px] text-muted-foreground">
                Incluye enganche de {formatMXN(n.down)} + {n.weeks} cuotas fijas de{' '}
                {formatMXN(n.weekly)}
            </div>
            {creditCost !== null && (
                <div className="flex justify-between pt-1.5">
                    <span className="text-muted-foreground">Costo del crédito</span>
                    <span className="font-semibold tabular-nums text-warning">
                        {formatMXN(creditCost)}
                    </span>
                </div>
            )}
            {savings !== null && (
                <div className="flex justify-end pt-1.5">
                    <PromoChip size="md">
                        Sin la promoción pagaría {formatMXN(n.down + savings.totalWithout)}
                    </PromoChip>
                </div>
            )}
            <QuoteBonusNote bonusCents={bonusCents} />
            {cashCents > 0 && (
                <>
                    <div className="mt-2 flex justify-between border-t border-border pt-2">
                        <span className="text-muted-foreground">De contado</span>
                        <span className="tabular-nums">+ {formatMXN(cashCents / 100)}</span>
                    </div>
                    <div className="flex justify-between pt-1.5 text-[13.5px] font-bold">
                        <span>Total que paga el cliente</span>
                        <span className="tabular-nums">{formatMXN(n.total + cashCents / 100)}</span>
                    </div>
                </>
            )}
        </div>
    );
}
